// src/Pages/Login.jsx
import React, { useState, useContext } from"react";
import { useNavigate, Link as RouterLink } from"react-router-dom";
import axios from"axios";
import API_BASE_URL from"../Config/config";
import { UserContext } from "./UserContext";
import { useTenant } from "../Context/TenantContext";
import {
 Grid,
 Box,
 Typography,
 TextField,
 Button,
 Link,
 IconButton,
 InputAdornment,
 Alert,
 GlobalStyles,
 CircularProgress,
} from"@mui/material";
import Visibility from"@mui/icons-material/Visibility";
import VisibilityOff from"@mui/icons-material/VisibilityOff";
import { ArrowRight } from "lucide-react";
import { ArrowTopRightOnSquareIcon, SparklesIcon, CodeBracketIcon } from "@heroicons/react/24/outline";
import Footer from "./Footer";

const isEmail = (s ="") => /\S+@\S+\.\S+/.test(s);

const Login = () => {
 const navigate = useNavigate();
 const { setUser } = useContext(UserContext);
 const { getLogoUrl, getCollegeName, tenant } = useTenant();

 // Royal Purple Gradient
 const premiumGradient = 'linear-gradient(135deg, #3F0071 0%, #5A189A 45%, #7B2CBF 100%)';

 const [usernameOrEmail, setUsernameOrEmail] = useState("");
 const [password, setPassword] = useState("");
 const [showPassword, setShowPassword] = useState(false);
 const [error, setError] = useState("");
 const [loading, setLoading] = useState(false);

 // ✅ Redirect if already logged in
 React.useEffect(() => {
 const storedUser = localStorage.getItem("user");
 if (storedUser) {
 navigate("/dashboard", { replace: true });
 }
 }, [navigate]);

 const handleClickShowPassword = () => setShowPassword((prev) => !prev);

 const handleLogin = async (e) => {
 e.preventDefault();
 setError("");

 const idInput = usernameOrEmail.trim();
 const pwdInput = password;

 if (!idInput || !pwdInput) {
 setError("Please enter your username/email and password.");
 return;
 }

 setLoading(true);
 try {
  // Backend accepts username OR email in"userId"
  const res = await axios.post(`${API_BASE_URL}/user/login`, {
   userId: idInput,
   password: pwdInput,
   tenantId: tenant?.id // Pass tenant context to backend
  }, {
   withCredentials: true // Ensure cookies are sent and stored
  });

 const payload = res?.data ?? {};
 const user = payload.user ?? payload.data ?? {};

 // Prefer readable username/handle coming from backend.
 // If the user typed a username (not an email) and backend didn’t echo it,
 // fall back to what they typed.
 const usernameId =
 user.userId ??
 user.user_id ??
 (!isEmail(idInput) ? idInput : null);

 // Keep DB primary key if present (uuid/int)
 const userPk = user.id ?? user._id ?? payload.id ?? null;

 if (!usernameId) {
 throw new Error("Login response missing userId (username).");
 }

 // ✅ Store session token for single login enforcement
 const sessionToken = payload.token || payload.sessionToken || payload.data?.sessionToken;
 if (sessionToken) {
  localStorage.setItem("session_token", sessionToken);
 // Set default Authorization header for all axios requests
 axios.defaults.headers.common.Authorization = `Bearer ${sessionToken}`;
 axios.defaults.headers.common['X-Session-Token'] = sessionToken;
 } else {
 console.warn('No session token received from login response');
 }

    // Persist for the rest of the app
    localStorage.setItem("user_id", String(usernameId)); // <-- what your forms should use
    if (userPk != null) localStorage.setItem("user_pk", String(userPk)); // optional
    localStorage.setItem("user", JSON.stringify(user));
    setUser(user);

    // Track login time for payment popup (used in ChatBot and Dashboard)
 const loginSessionKey = `login_session_start_${usernameId}`;
 localStorage.setItem(loginSessionKey, Date.now().toString());
 
 // Clear previous popup display count for new login session (both ChatBot and Dashboard)
 const chatbotPopupSessionKey = `chatbot_popup_session_${usernameId}`;
 const dashboardPopupSessionKey = `dashboard_popup_session_${usernameId}`;
 localStorage.removeItem(chatbotPopupSessionKey);
 localStorage.removeItem(dashboardPopupSessionKey);

 // Legacy token handling (optional)
 if (payload.token) {
 localStorage.setItem("auth_token", payload.token);
 }

 navigate("/dashboard");
 } catch (err) {
 const errorResponse = err.response?.data;
 const errorMsg = errorResponse?.error || errorResponse?.message || err.message ||"Login failed. Please try again.";
 
 // Check if account is locked
 if (err.response?.status === 403 && errorMsg.toLowerCase().includes('locked')) {
 // Redirect to locked account page with user ID
 const userId = idInput; // Use the input they provided
 navigate('/account-locked', { state: { userId } });
 return;
 }
 
 setError(errorMsg);
 } finally {
 setLoading(false);
 }
 };

  return (
<div className="min-h-screen bg-[#FDFCFE] flex flex-col md:flex-row font-body selection:bg-[#6A0DAD]/20 relative overflow-hidden">
  {/* Abstract background elements */}
  <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#7B2CBF]/5 to-transparent pointer-events-none"></div>
  <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#5A189A]/5 rounded-full blur-[100px] animate-pulse"></div>

  {/* Left Column: Premium Branding */}
  <div className="hidden lg:flex w-1/2 relative overflow-hidden items-center justify-center p-20"
    style={{ background: premiumGradient }}>
    <div className="absolute inset-0 opacity-20 pointer-events-none">
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/10 rounded-full blur-[120px] animate-pulse"></div>
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-purple-400/20 rounded-full blur-[120px] animate-pulse delay-1000"></div>
    </div>
    
    <div className="relative z-10 text-center max-w-lg">
      <div className="mb-10 transform hover:scale-110 transition-transform duration-500">
        <img 
          src={getLogoUrl()} 
          alt={getCollegeName()} 
          className="w-28 h-28 mx-auto rounded-[32px] shadow-2xl bg-white p-3 object-contain"
onError={(e) => { e.target.src = "/logo192.png"; }}
        />
      </div>
      <h1 className="text-6xl font-black text-white mb-6 tracking-tight leading-tight">
        Master Your <br />
        <span className="text-purple-200">Future Today</span>
      </h1>
      <p className="text-xl text-white/90 font-medium mb-12 leading-relaxed">
        Step into the AI-powered command center designed for the next generation of industry leaders.
      </p>
      
      <div className="grid grid-cols-2 gap-4">
        {[
          { label: 'AI Mentorship', icon: <SparklesIcon className="h-6 w-6" /> },
          { label: 'Live Coding', icon: <CodeBracketIcon className="h-6 w-6" /> },
          { label: 'Guided Paths', icon: <ArrowTopRightOnSquareIcon className="h-6 w-6" /> },
          { label: 'Job Ready', icon: <SparklesIcon className="h-6 w-6" /> }
        ].map((feat, i) => (
          <div key={i} className="bg-white/10 backdrop-blur-xl border border-white/20 p-5 rounded-2xl text-white font-bold text-sm flex items-center gap-4 hover:bg-white/20 transition-all cursor-default">
            <span className="text-purple-200">{feat.icon}</span>
            <span>{feat.label}</span>
          </div>
        ))}
      </div>
    </div>
  </div>

  {/* Right Column: Authentication Interface */}
  <div className="flex-1 flex flex-col justify-center items-center p-6 sm:p-12 lg:p-20 bg-[#FDFCFE] relative z-10">
    <div className="w-full max-w-md">
      <div className="lg:hidden mb-12 text-center">
        <img 
          src={getLogoUrl()} 
          alt={getCollegeName()} 
          className="w-20 h-20 mx-auto rounded-3xl shadow-xl bg-white p-2 mb-4"
onError={(e) => { e.target.src = "/logo192.png"; }}
        />
        <h2 className="text-4xl font-black text-[#1A1A1A] tracking-tight">{getCollegeName()}</h2>
      </div>

      <div className="mb-10 text-center lg:text-left">
        <h2 className="text-4xl sm:text-5xl font-black text-[#1A1A1A] tracking-tight mb-4">Welcome Back</h2>
        <p className="text-[#64748B] font-semibold text-lg">Enter your credentials to access your workspace.</p>
      </div>

      <form onSubmit={handleLogin} className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-bold text-[#1A1A1A] ml-1 uppercase tracking-wider">Email or Username</label>
          <input
            type="text"
            value={usernameOrEmail}
            onChange={(e) => setUsernameOrEmail(e.target.value)}
            placeholder="e.g. alex_learner"
            className="w-full bg-white border-2 border-[#F1F5F9] p-4 rounded-2xl focus:ring-4 focus:ring-[#6A0DAD]/5 focus:border-[#6A0DAD] transition-all outline-none font-bold text-[#1A1A1A] shadow-sm hover:border-[#E2E8F0]"
          />
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center ml-1">
            <label className="text-sm font-bold text-[#1A1A1A] uppercase tracking-wider">Password</label>
            <Link
              component={RouterLink}
              to="/forgot-password"
              sx={{ color: "#6A0DAD", fontSize: "0.875rem", fontWeight: 800, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
            >
              Forgot Password?
            </Link>
          </div>
          <div className="relative group">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-white border-2 border-[#F1F5F9] p-4 rounded-2xl focus:ring-4 focus:ring-[#6A0DAD]/5 focus:border-[#6A0DAD] transition-all outline-none font-bold text-[#1A1A1A] shadow-sm hover:border-[#E2E8F0]"
            />
            <button
              type="button"
              onClick={handleClickShowPassword}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#6A0DAD] transition-colors"
            >
              {showPassword ? <VisibilityOff className="w-5 h-5" /> : <Visibility className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {error && (
          <div className="p-4 rounded-2xl bg-red-50 border-2 border-red-100 text-red-600 text-sm font-bold flex items-center gap-3 animate-shake">
            <span className="w-2 h-2 rounded-full bg-red-600"></span>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !usernameOrEmail || !password}
          className="w-full bg-[#6A0DAD] text-white py-5 rounded-[20px] font-black text-xl shadow-xl hover:shadow-2xl hover:bg-[#5A189A] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-4"
        >
          {loading ? <CircularProgress size={28} color="inherit" thickness={5} /> : "Sign In"}
          {!loading && <ArrowRight className="h-6 w-6" />}
        </button>
      </form>

      <div className="mt-12 text-center lg:text-left pt-8 border-t-2 border-[#F1F5F9]">
        <p className="text-[#64748B] font-bold text-lg">
          New here?{" "}
          <button
            onClick={() => navigate("/register")}
            className="text-[#6A0DAD] font-black hover:text-[#5A189A] transition-colors underline decoration-2 underline-offset-4"
          >
            Create an account
          </button>
        </p>
      </div>
    </div>
  </div>
</div>
);
};




export default Login;